import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import ProcedurePlaceholder from "@/components/ProcedurePlaceholder";
import { HERO } from "@/data/procedureHeroImages";
import PatientTestimonials from "../components/PatientTestimonials";
import SEO from "@/components/SEO";
import { WHATSAPP_URL, TEL_URL, PHONE_DISPLAY } from "@/constants/contact";
import {
  ArrowRight,
  Award,
  ChevronRight,
  Clock,
  GraduationCap,
  MessageCircle,
  Phone,
  Minus,
  Plus,
  Syringe,
  Scissors,
  Snowflake,
  Zap,
  Bandage,
  Layers,
} from "lucide-react";

const PAGE_URL = "https://drvickyjain.com/keloid-treatment-mumbai";
const HYPERTROPHIC_PATH = "/hypertrophic-scar-treatment-in-mumbai";
const MOLE_PATH = "/mole-removal-mumbai";
const CYST_PATH = "/sebaceous-cyst-removal-mumbai";

const KELOID_FAQS = [
  {
    question: "Can a keloid be permanently removed?",
    answer:
      "Complete permanent removal is not reliably achievable for most keloids, particularly large or previously treated ones. Surgery alone produces recurrence in the large majority of cases. Combination therapy, surgery alongside steroid injections and other adjuvant treatments, significantly reduces recurrence risk and produces lasting improvement in most patients. The realistic goal is a keloid that is significantly smaller, flatter, less symptomatic, and maintained in a controlled state through ongoing management rather than complete disappearance.",
  },
  {
    question: "Will my keloid come back after surgery?",
    answer:
      "Without adjuvant treatment, yes, almost certainly. Keloid recurrence after simple excision alone is reported in 45 to 100 percent of cases in medical literature. With combination therapy, steroid injections started at the time of surgery and continued on a maintenance schedule, recurrence rates are significantly reduced but not eliminated. The location of the keloid affects recurrence risk. Earlobe keloids treated with combined excision, steroid injection, and pressure therapy have lower recurrence rates than chest or shoulder keloids.",
  },
  {
    question: "How many steroid injection sessions are needed?",
    answer:
      "Most patients require 4 to 8 sessions spaced 4 to 6 weeks apart during the initial treatment phase. After maximum improvement is achieved, maintenance sessions every 3 to 6 months may be recommended for active keloids with a history of growth. The total number of sessions depends on the keloid's response to treatment, its size, and how long the patient has had it.",
  },
  {
    question: "Is keloid treatment painful?",
    answer:
      "Steroid injections into the keloid produce a burning, pressure sensation during and briefly after injection. Keloid tissue is firm and the injection resistance is higher than in normal tissue. Local anesthesia is applied before injection in most cases. Cryotherapy produces a cold, stinging sensation during application. Surgical excision is performed under local anesthesia. Post-operative discomfort after surgery is mild and managed with standard pain medication.",
  },
  {
    question: "What is the difference between a keloid and a hypertrophic scar?",
    answer:
      "A keloid grows beyond the boundary of the original wound and does not regress spontaneously. A hypertrophic scar remains within the wound boundary and often flattens gradually over 1 to 2 years without treatment. The distinction matters because a hypertrophic scar often improves with time and conservative management, while a keloid requires active treatment. Misidentifying a keloid as a hypertrophic scar leads to undertreatment and continued growth.",
  },
  {
    question: "Can keloids be treated without surgery?",
    answer:
      "Yes. Many keloids, particularly earlobe keloids and smaller keloids in early stages of growth, respond well to intralesional steroid injections without surgery. The injections flatten the keloid, reduce symptoms, and in some cases produce near-complete resolution without any surgical intervention. Surgery is reserved for keloids that do not respond adequately to injections, or that are large enough that debulking is needed before injection therapy can be effective.",
  },
  {
    question: "Does treating a keloid make it worse?",
    answer:
      "Surgery alone, without adjuvant treatment, can make a keloid worse by stimulating further abnormal collagen production in the healing wound. This is why surgery alone is not recommended. When surgery is performed as part of a planned combination protocol with immediate adjuvant steroid injection, this risk is managed. Non-surgical treatments including steroid injections, silicone therapy, and cryotherapy do not carry this risk.",
  },
  {
    question: "Are there specific keloid-prone areas where treatment is more difficult?",
    answer:
      "Yes. Keloids on the chest, sternum, and upper back are among the most difficult to treat due to high skin tension in these areas and the large surface area they often cover. Keloids on the earlobes are generally the most responsive to treatment due to low skin tension and the technical feasibility of complete surgical excision with pressure therapy. Keloids on the jawline and shoulders have intermediate difficulty. Location is a significant factor in the treatment plan and the realistic outcome discussion.",
  },
];

const structuredData = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "MedicalCondition",
      name: "Keloid",
      description:
        "Abnormal proliferative scar extending beyond the original wound; managed with intralesional steroids, combination therapy including surgery with adjuvant treatment, cryotherapy, laser, and silicone-based care.",
      associatedAnatomy: [
        { "@type": "AnatomicalStructure", name: "Skin" },
        { "@type": "AnatomicalStructure", name: "Scar tissue" },
      ],
      possibleTreatment: [
        "Intralesional steroid injection",
        "Surgical excision with adjuvant therapy",
        "Cryotherapy",
        "Laser therapy",
        "Silicone gel and sheeting",
      ],
      url: PAGE_URL,
    },
    {
      "@type": "MedicalProcedure",
      name: "Keloid surgical excision with adjuvant therapy",
      description:
        "Surgical debulking of keloid scar combined with intralesional corticosteroid and maintenance protocol to reduce recurrence under plastic surgical care.",
      procedureType: "SurgicalProcedure",
      bodyLocation: { "@type": "AnatomicalStructure", name: "Skin" },
      medicalSpecialty: "https://schema.org/PlasticSurgery",
      url: PAGE_URL,
    },
    {
      "@type": "FAQPage",
      mainEntity: KELOID_FAQS.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: { "@type": "Answer", text: faq.answer },
      })),
    },
  ],
};

function KeloidJsonLd() {
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />;
}

function Breadcrumb() {
  const items = [
    { name: "Home", href: "/" },
    { name: "Reconstructive Surgery", href: "/reconstructive-surgery-in-mumbai" },
    { name: "Keloid Treatment", href: "/keloid-treatment-mumbai" },
  ];
  return (
    <nav className="bg-warm-white pt-24 pb-4" data-testid="breadcrumb">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <ol className="flex flex-wrap items-center gap-2 text-sm text-deep-navy/60">
          {items.map((item, index) => (
            <li key={item.name} className="flex items-center gap-2">
              {index > 0 && <ChevronRight className="w-3 h-3 text-gold" />}
              {index === items.length - 1 ? (
                <span className="text-deep-navy/70">{item.name}</span>
              ) : (
                <Link to={item.href} className="hover:text-gold transition-colors">
                  {item.name}
                </Link>
              )}
            </li>
          ))}
        </ol>
      </div>
    </nav>
  );
}

function HeroSection() {
  return (
    <section className="bg-warm-white pt-8 md:pt-10 pb-12 md:pb-20" data-testid="procedure-hero">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Scar Management</p>
            <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl text-premium-purple leading-[1.08] mb-4">Keloid Treatment</h1>
            <div className="w-12 h-[2px] bg-gold mb-5" />
            <p className="text-lg sm:text-xl text-deep-navy/65 leading-relaxed mb-7 max-w-xl font-light">
              Advanced Keloid Scar Management by Dr. Vicky Jain · Plastic &amp; Reconstructive Surgeon
            </p>
            <div className="flex flex-wrap gap-2">
              <span className="bg-premium-purple/10 px-3 py-1.5 rounded-full text-xs font-semibold text-premium-purple">Board Certified</span>
              <span className="bg-premium-purple/10 px-3 py-1.5 rounded-full text-xs font-semibold text-premium-purple">17+ Years Experience</span>
              <span className="bg-premium-purple/10 px-3 py-1.5 rounded-full text-xs font-semibold text-premium-purple">International Training</span>
            </div>
          </div>
          <div className="flex justify-center lg:justify-end">
            <ProcedurePlaceholder procedureName="Keloid Treatment" icon="🩹" size="hero"  imageSrc={HERO.keloidTreatment} imageAlt="Keloid treatment"/>
          </div>
        </div>
      </div>
    </section>
  );
}

function WhatIsSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20" data-testid="what-is-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Overview</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-6">What is a Keloid?</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              A keloid is an abnormal overgrowth of scar tissue that extends beyond the boundaries of the original wound. When a wound heals normally, the body produces collagen to repair the damaged tissue and the process stops once the wound is closed. In keloid formation, this process continues beyond what is needed, producing a raised, thickened, often shiny scar that grows larger than the original injury and does not stop growing on its own.
            </p>
            <p>
              Keloids are not dangerous but they cause significant problems. They are frequently itchy or painful, particularly in the early active growth phase. They can grow to be considerably larger than the wound that caused them, creating cosmetic concern that affects clothing choices and confidence. On the earlobes, they are among the most common cosmetic complaints after ear piercing. On the chest and shoulders, they develop from acne, surgical wounds, burns, and cuts and can be extensive.
            </p>
            <p>
              Dr. Vicky Jain manages keloids using combination therapy protocols. The single most important fact about keloid treatment is that surgery alone produces recurrence in the majority of cases. Keloids that are excised without adjuvant treatment almost always return, often larger than before. Effective keloid management requires a combination of approaches tailored to the size, location, and behavior of each keloid, with long-term follow-up to manage recurrence when it occurs.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function DrJainApproachSection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="dr-jain-approach-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Our Approach</p>
            <h2 className="font-serif text-3xl md:text-4xl text-white mb-8">Dr. Jain&apos;s Approach to Keloid Management</h2>
            <div className="space-y-6 text-white/75 leading-relaxed">
              <p>
                The goal in keloid treatment is control, not cure. Keloids cannot reliably be eliminated completely in the way a benign skin lesion can be excised. They can be significantly flattened, softened, reduced in size, made less symptomatic, and in many cases maintained in a stable state that does not progress. This is a meaningful and clinically significant outcome. Patients who understand this before beginning treatment have more realistic expectations and are better able to commit to the long-term follow-up that keloid management requires.
              </p>
              <p>
                Dr. Jain selects the treatment combination based on the keloid&apos;s size, location, age, and previous treatment history. A small, early earlobe keloid responds well to intralesional steroid injections alone. A large chest keloid that has been present for years and has been previously treated requires a more intensive combination including surgical debulking followed by immediate steroid injection and ongoing maintenance. A keloid that has recurred after previous surgery elsewhere requires the most comprehensive approach and honest discussion about what further treatment can achieve.
              </p>
            </div>
          </div>
          <div className="relative">
            <img src="/dr-jain.avif" alt="Dr. Vicky Jain" loading="lazy" decoding="async" className="w-full h-[420px] object-cover object-top" />
            <div
              className="absolute top-0 left-0 right-0 h-[420px] pointer-events-none hidden lg:block"
              style={{ background: "linear-gradient(to right, #3D2566 0%, rgba(61, 37, 102, 0) 15%)" }}
            />
            <div className="mt-4 text-center lg:text-left">
              <p className="font-serif text-lg text-white/80">Dr. Vicky Jain · Plastic &amp; Reconstructive Surgeon</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

const VS_HYPERTROPHIC_ROWS = [
  {
    label: "Growth Beyond Wound",
    keloid: "Yes. Extends beyond the original wound boundary into surrounding normal skin.",
    hypertrophic: "No. Remains within the boundaries of the original wound.",
  },
  {
    label: "Timeline",
    keloid: "Can continue growing for months to years. Does not regress spontaneously in most cases.",
    hypertrophic: "Often reaches maximum size within 6 months then gradually flattens over 1 to 2 years without treatment.",
  },
  {
    label: "Recurrence After Treatment",
    keloid: "High recurrence risk. Surgery alone produces recurrence in the majority of cases. Combination therapy reduces but does not eliminate this risk.",
    hypertrophic: "Low recurrence risk. Generally responds well to conservative management.",
  },
  {
    label: "Common Locations",
    keloid: "Earlobes, chest, shoulders, jawline, and upper back. Areas under tension or prone to inflammation.",
    hypertrophic: "Along any wound, most commonly surgical scars and burns.",
  },
  {
    label: "Treatment Approach",
    keloid: "Combination therapy required. Steroid injections, surgery when needed, adjuvant treatments, and long-term follow-up.",
    hypertrophic: "Silicone therapy, compression, and steroid injections for symptomatic cases.",
  },
];

function KeloidVsHypertrophicSection() {
  return (
    <section className="bg-[#FAFAF8] py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="keloid-vs-hypertrophic-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Understanding Your Options</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">
          Keloid vs. Hypertrophic Scar: An Important Distinction
        </h2>
        <div className="max-w-5xl mx-auto overflow-x-auto">
          <table className="w-full text-left border-collapse text-sm md:text-base">
            <thead>
              <tr className="border-b border-deep-navy/20">
                <th scope="col" className="py-4 pr-4 font-serif text-premium-purple font-normal w-[18%]" />
                <th scope="col" className="py-4 px-3 font-serif text-premium-purple bg-white/80">
                  Keloid
                </th>
                <th scope="col" className="py-4 pl-3 font-serif text-premium-purple bg-white/80">
                  <Link to={HYPERTROPHIC_PATH} className="hover:text-gold transition-colors">
                    Hypertrophic Scar
                  </Link>
                </th>
              </tr>
            </thead>
            <tbody>
              {VS_HYPERTROPHIC_ROWS.map((row) => (
                <tr key={row.label} className="border-b border-deep-navy/10">
                  <th scope="row" className="py-4 pr-4 font-medium text-premium-purple align-top">
                    {row.label}
                  </th>
                  <td className="py-4 px-3 text-deep-navy/70 align-top">{row.keloid}</td>
                  <td className="py-4 pl-3 text-deep-navy/70 align-top">{row.hypertrophic}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="max-w-5xl mx-auto mt-8 text-deep-navy/70 text-center md:text-left leading-relaxed">
          This distinction matters clinically because the two conditions require different treatment approaches and have different prognoses. A hypertrophic scar treated with silicone therapy and time often resolves without intervention. A keloid treated the same way does not. Misidentifying a keloid as a hypertrophic scar leads to undertreatment and continued growth.
        </p>
      </div>
    </section>
  );
}

function CausesRiskSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="causes-risk-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Causes & Risk Factors</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-6">What Causes Keloids and Who Is at Risk?</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Keloids form in response to skin trauma in individuals who are genetically predisposed to abnormal scar formation. The skin injury that triggers a keloid can be minor: ear piercing, a vaccination injection, a small cut, or an acne spot. The size of the original injury does not predict the size of the resulting keloid. Some patients develop extensive keloids from ear piercings while others heal from major surgery without any keloid formation. The predisposition is genetic and is significantly more common in patients with darker skin types.
            </p>
            <p>
              Common triggers include ear piercing, which is the most frequent cause of earlobe keloids; acne, particularly on the chest, shoulders, and jawline; surgical wounds, particularly those on the chest and upper body where skin tension is high; burns and deep cuts; and vaccination sites, particularly BCG vaccination on the upper arm. Patients who have had one keloid are at significantly elevated risk of forming keloids from subsequent skin injuries. This predisposition should inform decisions about elective procedures, piercings, and wound care after any skin trauma.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

const TREATMENT_OPTIONS = [
  {
    title: "Intralesional Steroid Injections",
    icon: Syringe,
    body:
      "The first-line treatment for most keloids. Triamcinolone acetonide is injected directly into the keloid at 4 to 6 week intervals. The steroid reduces inflammation, inhibits collagen production, and causes the keloid to soften and flatten progressively. Multiple sessions are required. Results are gradual. The most common side effects are skin thinning, hypopigmentation, and telangiectasia at the injection site if the steroid spreads beyond the keloid. Most effective for early, actively growing keloids and as adjuvant treatment alongside surgery for larger keloids.",
  },
  {
    title: "Surgical Excision with Adjuvant Therapy",
    icon: Scissors,
    body:
      "Used for larger keloids that do not respond adequately to injections alone, or that cause significant functional or cosmetic impairment. Surgery debulks the keloid but must be combined with adjuvant treatment to reduce recurrence. Steroid injections are started immediately post-operatively, typically on the day of surgery or within the first week, and continued on a maintenance schedule for 6 to 12 months. Surgery without adjuvant treatment produces recurrence in the large majority of cases and the recurrent keloid is frequently larger than the original.",
  },
  {
    title: "Silicone Gel and Sheeting",
    icon: Layers,
    body:
      "Applied continuously to the keloid and healing scar. Silicone hydrates the scar tissue, reduces tension on the wound surface, and has a direct effect on collagen remodeling. Most effective as a preventive and maintenance measure alongside other treatments rather than as a standalone cure for established keloids. Requires consistent use for 3 to 6 months to produce meaningful improvement.",
  },
  {
    title: "Cryotherapy",
    icon: Snowflake,
    body:
      "Liquid nitrogen is applied to freeze the keloid tissue, causing cell death and gradual tissue reduction. More effective for smaller keloids and frequently combined with steroid injections for a synergistic effect. Multiple sessions are required. Can cause temporary hypopigmentation, which is a concern in patients with darker skin types.",
  },
  {
    title: "Laser Therapy",
    icon: Zap,
    body:
      "Vascular lasers target the blood supply within the keloid to reduce redness, improve the surface texture, and reduce the active growth phase. Not a standalone keloid treatment but useful as an adjunct to improve cosmetic appearance and reduce symptoms alongside other treatments.",
  },
  {
    title: "Pressure Therapy",
    icon: Bandage,
    body:
      "Sustained pressure over the keloid site using compression garments or pressure clips, most commonly used for earlobe keloids after excision, reduces recurrence by maintaining compression that inhibits keloid growth. Used as part of a combination protocol particularly after ear keloid surgery.",
  },
];

function TreatmentOptionsSection() {
  return (
    <section className="bg-[#FAFAF8] py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="treatment-options-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Procedure Options</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Keloid Treatment Options</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {TREATMENT_OPTIONS.map((item) => (
            <div key={item.title} className="bg-white p-8 border border-[#E8E2D9]">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-gold/10 flex items-center justify-center flex-shrink-0" aria-hidden>
                  <item.icon className="w-6 h-6 text-gold" />
                </div>
                <div>
                  <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">{item.title}</h3>
                  <p className="text-deep-navy/70 leading-relaxed">{item.body}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CombinationApproachSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="combination-approach-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Our Protocol</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-6">The Combination Approach: Why It Matters</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Keloid recurrence after surgery alone is well-documented in the medical literature. Studies report recurrence rates of 45 to 100 percent after simple excision without adjuvant treatment. This is why no qualified plastic surgeon recommends surgery as the sole treatment for a keloid. The current evidence-based standard is excision combined with immediate intralesional steroid injection at the time of surgery, followed by a maintenance injection schedule over 6 to 12 months. This combination reduces recurrence rates to 50 percent or less, with some protocols achieving lower rates in selected locations.
            </p>
            <p>
              The specific combination used depends on the keloid. Earlobe keloids are managed differently from chest keloids. A first-presentation keloid is managed differently from one that has already recurred after treatment. Dr. Jain designs each treatment plan around the individual keloid&apos;s characteristics, location, previous treatment history, and the patient&apos;s skin type and overall risk profile for recurrence. Long-term follow-up is part of every keloid management plan because recurrence can occur months or years after apparent resolution.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function RecoveryResultsSection() {
  return (
    <section className="bg-[#FAFAF8] py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="recovery-results-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">What to Expect</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-6">What to Expect from Keloid Treatment</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Results from keloid treatment are gradual and cumulative. After the first steroid injection session, patients often notice reduced itching and some early softening of the keloid within 2 to 4 weeks. Flattening and size reduction accumulate over multiple sessions spaced 4 to 6 weeks apart. Most patients require 4 to 8 injection sessions over 6 to 12 months to achieve maximum improvement from injections alone.
            </p>
            <p>
              After surgical excision with adjuvant therapy, the treated area is observed during the immediate post-operative period and steroid injections begin as planned. The appearance of the treated site improves progressively over the following months. Most patients see significant improvement in the first 3 to 6 months. The maintenance injection schedule continues beyond this point to prevent recurrence.
            </p>
            <p>
              Complete disappearance of a keloid is not a realistic expectation for most patients, particularly for large or previously treated keloids. Significant flattening, reduction in symptoms, and improvement in cosmetic appearance are achievable and meaningful outcomes. The degree of improvement varies between patients based on skin type, keloid location, and individual collagen response to treatment.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function PreventionSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="prevention-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Prevention</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-6">Can Keloids Be Prevented?</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Patients with a known predisposition to keloid formation, either from a personal history of keloids or a family history, should take specific precautions. Elective procedures that create skin wounds in keloid-prone areas should be carefully considered. Ear piercings, particularly of the cartilage, are a common trigger for keloids and should be avoided by patients with known keloid tendency. Surgical wounds in these patients should be managed with preventive post-operative steroid injections and silicone therapy beginning as soon as the wound is healed.
            </p>
            <p>
              Early intervention matters. Treating a scar that is showing signs of abnormal thickening, redness, and continued growth in the first weeks after a wound heals, before it has established as a mature keloid, produces better outcomes than waiting until the keloid is large and established. Patients with keloid predisposition who notice any wound not healing with a flat, pale scar should seek assessment promptly rather than waiting to see how it develops.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function WhoShouldSeekSection() {
  return (
    <section className="bg-[#FAFAF8] py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="who-should-seek-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Am I a Candidate?</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-6">Who Should Seek Keloid Treatment?</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Any patient with a raised, growing, or symptomatic keloid that is causing cosmetic concern, itching, pain, or restriction of movement is appropriate for consultation. There is no minimum size. A small but growing earlobe keloid that is distressing the patient warrants early treatment because smaller keloids respond better to non-surgical management and are less likely to require surgical excision.
            </p>
            <p>
              Patients who have had keloid surgery elsewhere and experienced recurrence are appropriate candidates for reassessment and a more comprehensive combination treatment plan. Patients who are aware of their keloid predisposition and have developed a new abnormal scar should seek assessment early in the scar&apos;s development. Patients with very large keloids causing functional restriction, chronic pain, or severe psychological distress should understand before treatment begins that complete resolution is unlikely but meaningful improvement is achievable with consistent combination therapy and long-term follow-up.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function PatientResultsSection() {
  return <PatientTestimonials procedureName="Scar" />;
}

function FAQSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="faq-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Common Questions</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Frequently Asked Questions</h2>
        <div className="max-w-3xl mx-auto">
          <Accordion type="single" collapsible className="w-full">
            {KELOID_FAQS.map((faq, index) => (
              <AccordionItem key={faq.question} value={`keloid-${index}`} className="border-b border-deep-navy/10">
                <AccordionTrigger className="text-left font-serif text-lg text-premium-purple hover:no-underline hover:text-gold py-6 [&[data-state=open]>div>svg:first-child]:hidden [&[data-state=open]>div>svg:last-child]:block [&>svg]:hidden">
                  <span className="pr-4">{faq.question}</span>
                  <div className="flex-shrink-0">
                    <Plus className="w-5 h-5 text-gold" />
                    <Minus className="w-5 h-5 text-gold hidden" />
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-deep-navy/70 leading-relaxed pb-6">{faq.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
}

function ConsultationCTASection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="consultation-cta-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl text-center">
        <h2 className="font-serif text-3xl md:text-4xl text-warm-white mb-4">Book Your Keloid Consultation</h2>
        <p className="text-warm-white/70 mb-8 max-w-2xl mx-auto leading-relaxed">
          During your consultation, Dr. Jain assesses the keloid&apos;s size, location, growth pattern, and previous treatment history to design a combination treatment plan appropriate for your specific keloid. Realistic outcomes are discussed honestly, including what degree of improvement is achievable and what long-term maintenance involves. You leave with a clear plan and a realistic picture of what treatment produces for your specific situation.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
          <Button
            asChild
            className="bg-gold text-white hover:bg-gold-hover border-none rounded-none px-8 py-6 text-base"
            data-testid="cta-book-consultation"
          >
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
              Book Consultation
              <ArrowRight className="w-5 h-5 ml-2" />
            </a>
          </Button>
          <Button
          asChild
          variant="outline"
          className="border-warm-white text-warm-white hover:bg-warm-white hover:text-deep-navy rounded-none px-8 py-6 text-base"
          data-testid="cta-call"
        >
          <a href={TEL_URL}>
            <Phone className="w-5 h-5 mr-2" />
            Call {PHONE_DISPLAY}
          </a>
        </Button>
        </div>
      </div>
    </section>
  );
}

function RelatedProceduresSection() {
  const procedures = [
    {
      name: "Scar Revision",
      description: "Improving the appearance of scars that are not keloids but have healed with poor cosmetic outcome.",
      href: HYPERTROPHIC_PATH,
    },
    {
      name: "Mole Removal",
      description: "Assessment and removal of skin lesions alongside keloid management for patients with multiple skin concerns.",
      href: MOLE_PATH,
    },
    {
      name: "Sebaceous Cyst Removal",
      description: "Managing skin growths that occasionally develop alongside keloid-prone areas.",
      href: CYST_PATH,
    },
  ];
  return (
    <section className="bg-[#FAFAF8] py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="related-procedures-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Related Procedures</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Related Procedures</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {procedures.map((procedure) => (
            <div
              key={procedure.name}
              className="bg-white p-8 border border-[#E8E2D9] hover:shadow-lg transition-shadow duration-300 group"
            >
              <h3 className="font-serif text-xl text-premium-purple mb-3 font-semibold tracking-tight">{procedure.name}</h3>
              <p className="text-deep-navy/60 mb-6 leading-relaxed">{procedure.description}</p>
              <Link
                to={procedure.href}
                className="inline-flex items-center text-deep-navy font-medium hover:text-gold transition-colors"
              >
                Learn More →
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function KeloidTreatmentPage() {
  return (
    <>
      <SEO
        title="Keloid Treatment | Dr. Vicky Jain"
        description="Raised, itchy, growing keloid scars on ears, chest, or shoulders. Dr. Vicky Jain provides advanced combination keloid treatment. Book your consultation."
        canonical="/keloid-treatment-mumbai"
      />
      <KeloidJsonLd />
      <Breadcrumb />
      <HeroSection />
      <WhatIsSection />
      <DrJainApproachSection />
      <KeloidVsHypertrophicSection />
      <CausesRiskSection />
      <TreatmentOptionsSection />
      <CombinationApproachSection />
      <RecoveryResultsSection />
      <PreventionSection />
      <WhoShouldSeekSection />
      <PatientResultsSection />
      <FAQSection />
      <ConsultationCTASection />
      <RelatedProceduresSection />
    </>
  );
}
